import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { motion } from "framer-motion";
import { Check, Crop, ZoomIn } from "lucide-react";
import {
  AVATAR_MAX_ZOOM,
  centeredGeometry,
  cropRectOf,
  cropToBlob,
  displayedSize,
  panBy,
  zoomAround,
  type CropGeometry,
} from "../../lib/photo";

interface AvatarCropperProps {
  file: File;
  /** Vrai pendant l'envoi : la boîte reste ouverte mais se fige. */
  sending: boolean;
  onCancel: () => void;
  onConfirm: (photo: Blob) => void;
  onFailure: (message: string) => void;
}

interface Point {
  x: number;
  y: number;
}

/** Centre et écartement d'un ou deux doigts : un seul calcul pour glisser et pincer. */
const summarize = (points: Point[]) => {
  const total = points.reduce((sum, point) => ({ x: sum.x + point.x, y: sum.y + point.y }), {
    x: 0,
    y: 0,
  });
  const [first, second] = points;
  return {
    x: total.x / points.length,
    y: total.y / points.length,
    spread:
      first === undefined || second === undefined
        ? 0
        : Math.hypot(first.x - second.x, first.y - second.y),
  };
};

const UNREADABLE = "Cette image n’a pas pu être ouverte. Essaie une autre photo.";

export const AvatarCropper = ({
  file,
  sending,
  onCancel,
  onConfirm,
  onFailure,
}: AvatarCropperProps) => {
  const titleId = useId();
  const zoomId = useId();
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const [source, setSource] = useState<string | null>(null);
  const [geometry, setGeometry] = useState<CropGeometry | null>(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSource(url);
    setGeometry(null);
    // Le décodage garde les octets tant que l'élément vit : on rend l'URL au
    // navigateur dès que la boîte se ferme ou change de photo.
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    confirmRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCancelRef.current();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  /** Le curseur zoome depuis le centre du cadre, faute de point désigné. */
  const applyZoom = useCallback((zoom: number) => {
    setGeometry((current) => (current === null ? null : zoomAround(current, zoom, 0.5, 0.5)));
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return;
    // Écouteur natif et non passif : React pose `wheel` en passif sur la racine,
    // où `preventDefault` ne ferait rien et laisserait la page défiler.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const bounds = stage.getBoundingClientRect();
      if (bounds.width === 0) return;
      setGeometry((current) =>
        current === null
          ? null
          : zoomAround(
              current,
              current.zoom * Math.exp(-event.deltaY / 420),
              (event.clientX - bounds.left) / bounds.width,
              (event.clientY - bounds.top) / bounds.width,
            ),
      );
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (geometry === null || sending) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const tracked = pointers.current;
    if (!tracked.has(event.pointerId)) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width === 0) return;

    const before = summarize([...tracked.values()]);
    tracked.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const after = summarize([...tracked.values()]);
    const ratio = before.spread > 0 && after.spread > 0 ? after.spread / before.spread : 1;

    setGeometry((current) => {
      if (current === null) return null;
      const moved = panBy(
        current,
        (after.x - before.x) / bounds.width,
        (after.y - before.y) / bounds.width,
      );
      return ratio === 1
        ? moved
        : zoomAround(
            moved,
            moved.zoom * ratio,
            (after.x - bounds.left) / bounds.width,
            (after.y - bounds.top) / bounds.width,
          );
    });
  };

  const releasePointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const confirm = async () => {
    const image = imageRef.current;
    if (geometry === null || image === null || sending) return;
    try {
      onConfirm(await cropToBlob(image, cropRectOf(geometry)));
    } catch (cause: unknown) {
      onFailure(cause instanceof Error ? cause.message : UNREADABLE);
    }
  };

  const size = geometry === null ? { width: 1, height: 1 } : displayedSize(geometry);

  return (
    <motion.div
      className="avatar-cropper"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && event.button === 0 && !sending) onCancel();
      }}
    >
      <motion.div
        className="avatar-cropper__panel glass-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        initial={{ opacity: 0, y: 26, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
      >
        <div className="avatar-cropper__head">
          <span className="section-kicker"><Crop size={14} /> Cadrage</span>
          <h2 id={titleId}>Place ton visage dans le cadre</h2>
        </div>

        <div
          ref={stageRef}
          className="avatar-cropper__stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={releasePointer}
          onPointerCancel={releasePointer}
        >
          {source !== null && (
            <img
              ref={imageRef}
              className="avatar-cropper__image"
              src={source}
              alt=""
              draggable={false}
              style={{
                width: `${size.width * 100}%`,
                height: `${size.height * 100}%`,
                // Tant que les dimensions naturelles sont inconnues, l'image
                // serait étirée au carré : on attend le premier cadrage.
                opacity: geometry === null ? 0 : 1,
                // Pourcentages relatifs à l'image elle-même : c'est la seule
                // façon de garder le glissement sur le compositeur.
                transform:
                  geometry === null
                    ? undefined
                    : `translate(${(geometry.offsetX / size.width) * 100}%, ${(geometry.offsetY / size.height) * 100}%)`,
              }}
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (naturalWidth === 0 || naturalHeight === 0) {
                  onFailure(UNREADABLE);
                  return;
                }
                setGeometry(centeredGeometry(naturalWidth, naturalHeight));
              }}
              onError={() => onFailure(UNREADABLE)}
            />
          )}
          <span className="avatar-cropper__mask" aria-hidden="true" />
        </div>

        <p className="social-hint avatar-cropper__hint">
          Glisse la photo pour la déplacer, pince ou utilise le curseur pour zoomer.
        </p>

        <label className="field-label avatar-cropper__zoom-label" htmlFor={zoomId}>
          <ZoomIn size={14} aria-hidden="true" /> Zoom
        </label>
        <input
          id={zoomId}
          className="avatar-cropper__zoom"
          type="range"
          min={1}
          max={AVATAR_MAX_ZOOM}
          step={0.01}
          value={geometry?.zoom ?? 1}
          disabled={geometry === null || sending}
          onChange={(event) => applyZoom(Number(event.target.value))}
        />

        <div className="avatar-cropper__actions">
          <button
            ref={confirmRef}
            className="primary-button"
            type="button"
            disabled={geometry === null || sending}
            onClick={() => void confirm()}
          >
            {sending ? <span className="button-spinner" aria-hidden="true" /> : <Check size={16} />}
            Utiliser cette photo
          </button>
          <button
            className="ghost-button"
            type="button"
            disabled={sending}
            onClick={onCancel}
          >
            Annuler
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
